import React, { useEffect, useRef, useState } from "react";
import "./ChatWindow.css";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import {
  faCopy,
  faVolumeUp,
  faMicrophone,
  faPaperPlane,
  faStop,
} from "@fortawesome/free-solid-svg-icons";
import { useParentContext } from "../context/ParentContext";
import useAdkRequestResponse from "../hooks/useAdkRequestResponse";

const ChatWindow = ({ currentUser, userRole }) => {
  const [newMessage, setNewMessage] = useState("");
  const messagesContainerRef = useRef(null);
  const { language } = useParentContext();
  const languageCode =
    language === "or" || language === "as" ? "en-IN" : language + "-IN";
  let parent_id = null;
  let pediatrician_id = null;
  if (userRole === "parent") {
    parent_id = currentUser.parent_id;
  } else if (userRole === "pediatrician") {
    pediatrician_id = currentUser.pediatrician_id;
  }
  const {
    messages,
    isTyping,
    isConnected,
    isAudioMode,
    isRecording,
    startVoice,
    stopVoice,
    sendText,
    reconnect,
    currentlyPlayingId
  } = useAdkRequestResponse(
    parent_id,
    pediatrician_id,
    null,
    languageCode,
    userRole,
  ); // Removed autoConnect prop

  useEffect(() => {
    if (messagesContainerRef.current) {
      messagesContainerRef.current.scrollTop =
        messagesContainerRef.current.scrollHeight;
    }
  }, [messages, isTyping]);

  const handleSubmit = (e) => {
    if (e) e.preventDefault();

    if (newMessage.trim()) {
      sendText(newMessage);
      setNewMessage("");
    }
  };

  const handleKeyPress = (e) => {
    if (e.key === "Enter" && !e.shiftKey) {
      e.preventDefault();
      handleSubmit();
    }
  };

  const handleVoiceToggle = () => {
    console.log("🎛️ [VOICE TOGGLE] Button clicked");
    console.log("📊 [VOICE TOGGLE] Current state:", {
      isAudioMode,
      isConnected,
      isRecording,
    });

    if (isAudioMode) {
      stopVoice(); // This now stops recording and sends the audio request
    } else {
      startVoice(); // This now only starts recording
    }
  };

  const copyToClipboard = (text) => {
    navigator.clipboard
      .writeText(text)
      .then(() => {
        console.log("✅ Text copied");
      })
      .catch((err) => {
        console.error("❌ Failed to copy:", err);
      });
  };

  const LishaSpeakingIcon = () => {
    return (
      <div className="lisha-speaking">
        <div className="bar bar1"></div>
        <div className="bar bar2"></div>
        <div className="bar bar3"></div>
        <div className="bar bar4"></div>
        <div className="bar bar5"></div>
      </div>
    );
  };

  return (
    <div className="chat-window__container">
      <div className="chat-window__wrapper">
        <header className="chat-window__header">
          <h1 className="chat-window__title">
            I'm Lisha, your digital pediatrician
          </h1>
          <p className="chat-window__subtitle">
            Thriving Children.Empowering Parents.
          </p>
        </header>

        <div className="chat-window__messages" ref={messagesContainerRef}>
          {messages.length === 0 && (
            <div className="chat-window__welcome-message">
              <h2 className="chat-window__welcome-title">
                Ask me anything about your child's health!
              </h2>
              <p className="chat-window__welcome-text">
                Start a conversation by typing a message or enabling voice mode.
              </p>
            </div>
          )}
          {messages.map((message, index) => (
            <div
              key={message.id || index}
              className={`chat-window__message-wrapper ${
                message.role === "user"
                  ? "chat-window__message-wrapper--user"
                  : "chat-window__message-wrapper--agent"
              }`}
            >
              <div
                className={`chat-window__message ${
                  message.role === "user"
                    ? "chat-window__message--user"
                    : "chat-window__message--agent"
                }`}
              >
                {message.role === "model" && message.hasAudio && (
                  <div
                    className="chat-window__audio-icon"
                    title="Audio response"
                  >
                    {currentlyPlayingId === message.id ? (
                      <span className="model-audio-message">
                        <FontAwesomeIcon icon={faVolumeUp} />
                        <LishaSpeakingIcon />
                      </span>
                    ) : (
                      <span className="model-audio-message">
                        <FontAwesomeIcon icon={faVolumeUp} />
                        <span>Voice message</span>
                      </span>
                    )}
                  </div>
                )}
                <span className="chat-window__message-text">
                  {message.text}
                </span>
                {message.role === "model" && !message.hasAudio && (
                  <button
                    className="chat-window__copy-button"
                    onClick={() => copyToClipboard(message.text)}
                    title="Copy to clipboard"
                  >
                    <FontAwesomeIcon icon={faCopy} />
                  </button>
                )}
              </div>
            </div>
          ))}
          {isTyping && (
            <div className="chat-window__message-wrapper chat-window__message-wrapper--agent">
              <div className="chat-window__typing-indicator">
                <span></span>
                <span></span>
                <span></span>
              </div>
            </div>
          )}
        </div>

        <form className="chat-window__input-form" onSubmit={handleSubmit}>
          <input
            type="text"
            className="chat-window__message-input"
            value={newMessage}
            onChange={(e) => setNewMessage(e.target.value)}
            onKeyDown={handleKeyPress}
            placeholder="Type your message here..."
            autoComplete="off"
            disabled={!isConnected || isRecording || isTyping}
          />
          <div className="chat-window__buttons">
            <button
              type="button"
              className={`chat-window__voice-button ${
                isAudioMode ? "chat-window__voice-button--active" : ""
              }`}
              onClick={handleVoiceToggle}
              title={isAudioMode ? "Stop Voice" : "Enable Voice"}
              disabled={!isConnected || isTyping}
            >
              <FontAwesomeIcon icon={isRecording ? faStop : faMicrophone} />
            </button>
            <button
              type="submit"
              className="chat-window__send-button"
              disabled={!isConnected || !newMessage.trim() || isRecording}
              title="Send message"
            >
              <FontAwesomeIcon icon={faPaperPlane} />
            </button>
          </div>
        </form>
      </div>
    </div>
  );
};

export default ChatWindow;
